'use client';

import React, { createContext, useContext, useState, useCallback, ReactNode } from 'react';

// Fixed header height: py-4 (16px top + 16px bottom) + h-10 logo (40px) = ~72px
export const HEADER_HEIGHT = '4.5rem'; // 72px

interface HeaderContextType {
  isHeaderVisible: boolean;
  setIsHeaderVisible: (visible: boolean) => void;
  hideHeader: () => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export function HeaderProvider({ children }: { children: ReactNode }) {
  // Default to visible, will be set via useState on client
  const [isHeaderVisible, setIsHeaderVisible] = useState(true);

  const hideHeader = useCallback(() => {
    // Only hide if currently visible
    setIsHeaderVisible((prev) => {
      if (prev) {
        return false;
      }
      return prev;
    });
  }, []);

  // Provide default context value that's safe for SSR
  const contextValue = React.useMemo(
    () => ({ isHeaderVisible, setIsHeaderVisible, hideHeader }),
    [isHeaderVisible, setIsHeaderVisible, hideHeader]
  );

  return (
    <HeaderContext.Provider value={contextValue}>
      {children}
    </HeaderContext.Provider>
  );
}

// No-op functions for default context values
const noopSetIsHeaderVisible = (_visible: boolean) => {
  // No-op: HeaderProvider not available
};

const noopHideHeader = () => {
  // No-op: HeaderProvider not available
};

export function useHeader() {
  const context = useContext(HeaderContext);
  if (context === undefined) {
    // Return default values instead of throwing error for better resilience
    // This allows components to work even if HeaderProvider is not available
    return {
      isHeaderVisible: true,
      setIsHeaderVisible: noopSetIsHeaderVisible,
      hideHeader: noopHideHeader,
    };
  }
  return context;
}

